import { createContext, ReactNode, useEffect, useReducer } from 'react';
// utils
import axios from '../utils/axios';
import { isValidToken, isImpersonationToken, setSession } from '../utils/jwt';
// @types
import { ActionMap, AuthState, AuthUser, JWTContextType } from '../@types/auth';
// services
import { logoutSso } from '../services/auth';

// ----------------------------------------------------------------------

enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  Register = 'REGISTER',
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
  [Types.Login]: {
    user: AuthUser;
  };
  [Types.Logout]: undefined;
  [Types.Register]: {
    user: AuthUser;
  };
};

export type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
      };
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };

    case 'REGISTER':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };

    default:
      return state;
  }
};

const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(JWTReducer, initialState);

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    try {
      const accessToken = window.localStorage.getItem('accessToken');

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const response = await axios.get('/app/auth/whoami');
        const { user } = response.data.data;
        const { transaction_id, company } = user;

        if (
          company.pixel_fired != null &&
          company.pixel_fired === 0 &&
          process.env.REACT_APP_ENV === 'prod'
        ) {
          try {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'conversion_paid',
              transaction_id: transaction_id || '',
            });

            await axios.patch('/app/admin/company/pixel-fired');
          } catch (err) {
            console.error(err);
          }
        }

        // Push user email to GTM dataLayer
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'userData', userEmail: user.email });

        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: true,
            user,
          },
        });
      } else {
        localStorage.removeItem('impersonation_return_url');

        // Ensure any leftover JWT (e.g. present but expired / malformed) is
        // fully removed from localStorage and the axios Authorization header.
        setSession(null);

        // Push userData event without email for unauthenticated users
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'userData', userEmail: undefined });

        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (err) {
      console.error(err);
      localStorage.removeItem('impersonation_return_url');

      // Clear the stale JWT from localStorage and the axios Authorization
      // header so subsequent API calls (e.g. SSO logout from the disabled
      // page) are not poisoned by a token that the backend already rejected.
      // Without this, EnsureUserIsActive intercepts every request carrying
      // the stale Bearer and returns 401 before the target controller runs.
      setSession(null);

      // Push userData event without email on auth failure
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'userData', userEmail: undefined });

      dispatch({
        type: Types.Initial,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  };

  // BIZH-1191 — SSO handoff hydration.
  // Called by SsoCallback after a successful /api/auth/sso/exchange response.
  // The exchange endpoint returns a byte-identical shape to login2fa(), so the
  // caller already holds the access_token and user object — no second network
  // call is needed. This mirrors the setSession + dispatch LOGIN steps that
  // login() performs after its credential POST, without the credential POST.
  //
  // BIZH-1195 — mark the session as SSO so logout() can branch correctly.
  const ssoLogin = (accessToken: string, user: AuthUser): void => {
    localStorage.setItem('auth_method', 'sso');
    setSession(accessToken);

    // Push user email to GTM dataLayer (mirrors login() behaviour)
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'userData', userEmail: (user as any)?.email });

    dispatch({
      type: Types.Login,
      payload: { user },
    });
  };

  const login = async (email: string, password: string) => {
    const LOGIN_URL = process.env.REACT_APP_LOGIN_URL || 'login-url-not-set';

    const response = await axios.post(LOGIN_URL, {
      email,
      password,
    });
    const { access_token, user } = response.data;
    const accessToken = access_token;

    setSession(accessToken);

    // Push user email to GTM dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'userData', userEmail: user.email });

    dispatch({
      type: Types.Login,
      payload: {
        user,
      },
    });
  };

  const impersonateLogin = async (token: string, returnUrl: string) => {
    if (!isValidToken(token)) {
      throw new Error('Invalid or expired impersonation token');
    }

    if (!isImpersonationToken(token)) {
      throw new Error('Token does not contain impersonation claims');
    }

    localStorage.setItem('impersonation_return_url', returnUrl);
    setSession(token);

    // The token from the redirect is a short-lived (2 min) handoff token.
    // Exchange it immediately for a longer-lived impersonation session token so
    // that filling in a long form (e.g. a performance review) does not outlive
    // the credential. If the exchange fails we keep the handoff token — the
    // axios response interceptor will retry the refresh on the first 401.
    try {
      const refreshed = await axios.post('/app/backoffice/impersonate/refresh');
      setSession(refreshed.data.access_token);
    } catch (err) {
      console.error('Failed to exchange impersonation handoff token:', err);
    }

    const response = await axios.get('/app/auth/whoami');
    const { user } = response.data.data;

    dispatch({
      type: Types.Login,
      payload: {
        user,
      },
    });
  };

  const leaveImpersonation = async () => {
    const returnUrl = localStorage.getItem('impersonation_return_url');

    try {
      await axios.post('/app/backoffice/impersonate/leave');
    } catch (error) {
      console.error('Failed to call leave impersonation endpoint:', error);
    }

    setSession(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('impersonation_return_url');
    localStorage.removeItem('enabledUsers');
    delete axios.defaults.headers.common.Authorization;

    dispatch({ type: Types.Logout });

    if (returnUrl) {
      window.location.href = returnUrl;
    }
  };

  const isImpersonating = !!window.localStorage.getItem('impersonation_return_url');

  const register = async (
    email: string,
    password: string,
    first_name: string,
    last_name: string,
    company_name: string,
    password_confirmation: string,
    is_trial: boolean
  ) => {
    const response = await axios.post('/app/auth/register', {
      first_name,
      last_name,
      email,
      password,
      company_name,
      password_confirmation,
      is_trial,
    });
    const { access_token, user } = response.data;
    const accessToken = access_token;
    setSession(accessToken);

    // Push user email to GTM dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'userData', userEmail: user.email });

    dispatch({
      type: Types.Register,
      payload: {
        user,
      },
    });
  };

  const registerFinish = async (
    tier_id: string,
    card_holder_name: string,
    card_num: string,
    exp_year: string,
    exp_month: string,
    cvv: string,
    captcha_token: string,
    did_agree: string,
    is_trial: boolean,
    register_token: string
  ) => {
    const response = await axios.post('/app/auth/register_finish', {
      tier_id,
      card_holder_name,
      card_num,
      exp_year,
      exp_month,
      cvv,
      captcha_token,
      did_agree,
      is_trial,
      register_token,
    });
    const { access_token, user } = response.data;
    const accessToken = access_token;

    setSession(accessToken);

    // Push user email to GTM dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'userData', userEmail: user.email });

    dispatch({
      type: Types.Register,
      payload: {
        user,
      },
    });
  };

  // BIZH-1195 — extracted helper so both the SSO and legacy branches share
  // the same localStorage teardown without duplication.
  const clearLocalAuthState = () => {
    setSession(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('enabledUsers');
    localStorage.removeItem('impersonation_return_url');
    localStorage.removeItem('auth_method');
    delete axios.defaults.headers.common.Authorization;

    // Clear user email from GTM dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ userEmail: undefined });
  };

  // BIZH-1195 — branches on auth_method to call POST /api/auth/sso/logout
  // before clearing local state. On any backend failure the catch block falls
  // through to local-only cleanup so the UI never deadlocks.
  const logout = async () => {
    const isSsoSession = localStorage.getItem('auth_method') === 'sso';

    if (isSsoSession) {
      try {
        const { logoutUrl } = await logoutSso();
        clearLocalAuthState();
        // Browser navigation — the React-Router navigate() in the caller
        // (AccountPopover.handleLogout) becomes a no-op after this.
        window.location.href = logoutUrl;
        return;
      } catch {
        // Backend unreachable — fall through to local-only logout so the UX
        // does not deadlock. The Keycloak session may survive in this case;
        // the user can re-trigger a clean logout from /auth/login if needed.
      }
    }

    clearLocalAuthState();
    dispatch({ type: Types.Logout });
  };

  const accessToken = () => {
    let tmpToken = window.localStorage.getItem('accessToken');
    let token = tmpToken ? tmpToken : '';

    return token;
  };

  return (
    <>
      <AuthContext.Provider
        value={{
          ...state,
          method: 'jwt',
          isImpersonating,
          accessToken,
          initialize,
          login,
          ssoLogin,
          impersonateLogin,
          leaveImpersonation,
          logout,
          register: register as any,
          registerFinish,
        }}
      >
        {children}
      </AuthContext.Provider>
    </>
  );
}

export { AuthContext, AuthProvider };
